import { Currency, CurrencyAmount, RUSDY, USDY, WNATIVE } from '@pancakeswap/sdk'
import { useMemo } from 'react'
import { useTranslation } from '@pancakeswap/localization'
import tryParseAmount from '@pancakeswap/utils/tryParseAmount'
import { useTransactionAdder } from 'state/transactions/hooks'
import { useCurrencyBalance } from 'state/wallet/hooks'
import useAccountActiveChain from 'hooks/useAccountActiveChain'
import BigNumber from 'bignumber.js'
import { useSingleCallResult } from 'state/multicall/hooks'
import { Field } from 'state/swap/actions'
import { useRUSDYContract, useWNativeContract } from './useContract'
import { useCallWithGasPrice } from './useCallWithGasPrice'

export enum WrapType {
  NOT_APPLICABLE,
  WRAP,
  UNWRAP,
}

const NOT_APPLICABLE = { wrapType: WrapType.NOT_APPLICABLE }
/**
 * Given the selected input and output currency, return a wrap callback
 * @param inputCurrency the selected input currency
 * @param outputCurrency the selected output currency
 * @param typedValue the user input value
 */
export default function useWrapCallback(
  inputCurrency: Currency | undefined,
  outputCurrency: Currency | undefined,
  typedValue: string | undefined,
): { wrapType: WrapType; execute?: undefined | (() => Promise<void>); inputError?: string } {
  const { t } = useTranslation()
  const { account, chainId } = useAccountActiveChain()
  const { callWithGasPrice } = useCallWithGasPrice()
  const wbnbContract = useWNativeContract()
  const rusdyContract = useRUSDYContract()
  const balance = useCurrencyBalance(account ?? undefined, inputCurrency)
  // we can always parse the amount typed as the input currency, since wrapping is 1:1
  const inputAmount = useMemo(() => tryParseAmount(typedValue, inputCurrency), [inputCurrency, typedValue])
  const addTransaction = useTransactionAdder()

  return useMemo(() => {
    if (!wbnbContract || !rusdyContract || !chainId || !inputCurrency || !outputCurrency) return NOT_APPLICABLE

    const sufficientBalance = inputAmount && balance && !balance.lessThan(inputAmount)

    // 修改的地方
    if (USDY[chainId]?.equals(inputCurrency) && RUSDY[chainId]?.equals(outputCurrency)) {
      return {
        wrapType: WrapType.WRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  const txReceipt = await callWithGasPrice(rusdyContract, 'wrap', [inputAmount.quotient])
                  const amount = inputAmount.toSignificant(6)
                  const native = inputCurrency.symbol
                  const wrap = outputCurrency.symbol
                  addTransaction(txReceipt, {
                    summary: `Wrap ${amount} ${native} to ${wrap}`,
                    translatableSummary: { text: 'Wrap %amount% %native% to %wrap%', data: { amount, native, wrap } },
                    type: 'wrap',
                  })
                } catch (error) {
                  console.error('Could not deposit', error)
                }
              }
            : undefined,
        inputError: sufficientBalance
          ? undefined
          : t('Insufficient %symbol% balance', { symbol: inputCurrency.symbol }),
      }
    }
    if (USDY[chainId]?.equals(outputCurrency) && RUSDY[chainId]?.equals(inputCurrency)) {
      return {
        wrapType: WrapType.UNWRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  const txReceipt = await callWithGasPrice(rusdyContract, 'unwrap', [inputAmount.quotient])
                  const amount = inputAmount.toSignificant(6)
                  const wrap = inputCurrency.symbol
                  const native = outputCurrency.symbol
                  addTransaction(txReceipt, {
                    summary: `Unwrap ${amount} ${wrap} to ${native}`,
                    translatableSummary: { text: 'Unwrap %amount% %wrap% to %native%', data: { amount, wrap, native } },
                  })
                } catch (error) {
                  console.error('Could not withdraw', error)
                }
              }
            : undefined,
        inputError: sufficientBalance
          ? undefined
          : t('Insufficient %symbol% balance', { symbol: inputCurrency.symbol }),
      }
    }

    // 修改的地方
    if (inputCurrency?.isNative && WNATIVE[chainId]?.equals(outputCurrency)) {
      const errorText = inputAmount
        ? sufficientBalance
          ? undefined
          : t('Insufficient %symbol% balance', { symbol: inputCurrency.symbol })
        : t('Enter Amount', { symbol: inputCurrency.symbol })

      return {
        wrapType: WrapType.WRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  const txReceipt = await callWithGasPrice(wbnbContract, 'deposit', undefined, {
                    value: inputAmount.quotient,
                  })
                  const amount = inputAmount.toSignificant(6)
                  const native = inputCurrency.symbol
                  const wrap = WNATIVE[chainId].symbol
                  addTransaction(txReceipt, {
                    summary: `Wrap ${amount} ${native} to ${wrap}`,
                    translatableSummary: { text: 'Wrap %amount% %native% to %wrap%', data: { amount, native, wrap } },
                    type: 'wrap',
                  })
                } catch (error) {
                  console.error('Could not deposit', error)
                }
              }
            : undefined,
        inputError: errorText,
        // inputError: sufficientBalance
        //   ? undefined
        //   : t('Insufficient %symbol% balance', { symbol: inputCurrency.symbol }),
      }
    }
    if (WNATIVE[chainId]?.equals(inputCurrency) && outputCurrency?.isNative) {
      return {
        wrapType: WrapType.UNWRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  const txReceipt = await callWithGasPrice(wbnbContract, 'withdraw', [inputAmount.quotient])
                  const amount = inputAmount.toSignificant(6)
                  const wrap = WNATIVE[chainId].symbol
                  const native = outputCurrency.symbol
                  addTransaction(txReceipt, {
                    summary: `Unwrap ${amount} ${wrap} to ${native}`,
                    translatableSummary: { text: 'Unwrap %amount% %wrap% to %native%', data: { amount, wrap, native } },
                  })
                } catch (error) {
                  console.error('Could not withdraw', error)
                }
              }
            : undefined,
        inputError: sufficientBalance
          ? undefined
          : t('Insufficient %symbol% balance', { symbol: inputCurrency.symbol }),
      }
    }
    return NOT_APPLICABLE
  }, [wbnbContract, chainId, inputCurrency, outputCurrency, t, inputAmount, balance, addTransaction, callWithGasPrice])
}

export function useIsWrapping(currencyA: Currency | undefined, currencyB: Currency | undefined, value?: string) {
  const { wrapType } = useWrapCallback(currencyA, currencyB, value)
  return wrapType !== WrapType.NOT_APPLICABLE
}

export function useRUSDYWrap(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  field?: Field,
  value?: string,
): { inputAmount: string; outputAmount: string; isRUSDWrap: boolean } {
  const { chainId } = useAccountActiveChain()
  const rusdyContract = useRUSDYContract()

  const BPS_DENOMINATOR = 10000
  let isRUSDYWrap = false
  let funcName = ''
  let valueBn = BigInt(0)

  let div = 1

  if (USDY[chainId]?.equals(currencyA) && RUSDY[chainId]?.equals(currencyB)) {
    isRUSDYWrap = true
    // getRUSDYByShares _USDYAmount => _rUSDYAmount
    valueBn = BigInt(
      new BigNumber(value || '0')
        .multipliedBy(new BigNumber(10).pow(18))
        .multipliedBy(BPS_DENOMINATOR)
        .toFixed(0, BigNumber.ROUND_DOWN),
    )
    if (valueBn > 0n && field === Field.INPUT) {
      funcName = 'getRUSDYByShares'
    }
  }
  if (USDY[chainId]?.equals(currencyB) && RUSDY[chainId]?.equals(currencyA)) {
    isRUSDYWrap = true
    // getSharesByRUSDY , _rUSDYAmount=> _USDYAmount
    valueBn = BigInt(
      new BigNumber(value || '0').multipliedBy(new BigNumber(10).pow(18)).toFixed(0, BigNumber.ROUND_DOWN),
    )
    if (valueBn > 10_000n && field === Field.INPUT) {
      funcName = 'getSharesByRUSDY'
      div = BPS_DENOMINATOR
    }
  }
  const { result } = useSingleCallResult({
    contract: funcName ? rusdyContract : null,
    functionName: funcName as any,
    args: [valueBn],
  })

  return useMemo(() => {
    if (field === Field.INPUT) {
      if (currencyB && isRUSDYWrap && typeof result !== 'undefined') {
        return {
          outputAmount: new BigNumber(result.toString()).div(1e18).div(div).dp(6, BigNumber.ROUND_DOWN).toFixed(),
          inputAmount: value,
          isRUSDWrap: isRUSDYWrap,
        }
      }
      return {
        inputAmount: value,
        outputAmount: '',
        isRUSDWrap: isRUSDYWrap,
      }
    }
    return {
      inputAmount: '',
      outputAmount: value,
      isRUSDWrap: isRUSDYWrap,
    }
  }, [result, chainId, isRUSDYWrap, value, field, div])
}
